// package
import { Fragment, useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Col,
  Row,
  OverlayTrigger,
  Tooltip,
  Form,
  Spinner,
} from "react-bootstrap";
import Select from "react-select";
import MaskedInput from "react-text-mask-legacy";
import moment from "moment";
import AmlHashForm from "aml-hash-form";

// component
import PageBreadcrumb from "elements/System/PageBreadcrumb";
import NotificationPreference from "elements/System/NotificationPreference";
import AgDelete from "elements/System/AgDelete";
import AgStatus from "elements/System/AgStatus";
import { api, common, config } from "includes";

export default function UserForm() {
  // const
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  let mode = searchParams.get("mode");
  let id = searchParams.get("id");

  // state
  const [process, setProcess] = useState<any>({ result: 100 });
  const [confirm, setConfirm] = useState<any>({ id: null, type: null });
  const [submit, setSubmit] = useState<any>("");
  const [resultList, setResultList] = useState<any>(null);
  const [permission] = useState(common.getUserPermission());

  // form
  const formFieldsA = {
    firstName: { validate: ["req#First Name is required"] },
    lastName: { Validate: ["req#Last Name is required"] },
    email: {
      validate: [
        "req#Email is required",
        "regExp#Please Enter valid email address",
        "custom#Email already exists"
      ],
      pattern: /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/,
    },
    title: { validate: [] },
    mobile: { validate: ["req#Mobile is required"] },
    role: { validate: ["req#Role is required"], obj: "value" },
    password: { validate: ["req#Password is required"] },
    verified: { validate: [] },
    active: { validate: [] },
  };

  const {
    fields: fieldsA,
    errors: errorsA,
    handleChange: handleChangeA,
    handleSubmit: handleSubmitA,
    setValue: setValueA,
    setMultiValue: setMultiValueA,
  } = AmlHashForm(formFieldsA);

  const formFieldsB = {
    firstName: { validate: ["req#First Name is required"] },
    lastName: { validate: ["req#Last Name is required"] },
    email: { validate: ["req#Email is required"] },
    title: { validate: ["req#Title is required"] },
    mobile: { validate: ["req#Mobile is required"] },
    role: { validate: ["req#Role is required"], obj: "value" },
    password: { validate: [] },
    verified: { validate: [] },
    active: { validate: [] },
  };

  const {
    fields: fieldsB,
    errors: errorsB,
    handleChange: handleChangeB,
    handleSubmit: handleSubmitB,
    setValue: setValueB,
    setMultiValue: setMultiValueB,
  } = AmlHashForm(formFieldsB);

  // effect
  useEffect(() => {
    if (!permission?.includes("usr")) {
      common.redirect("app/home");
    } else {
      if (id) {
        getData();
      } else {
        processState("result", 200);
      }
    }
  }, []);

  useEffect(() => {
    if (id) {
      getData();
    } else {
      processState("result", 200);
    }
  }, [mode]);

  // api
  const getData = () => {
    processState("result", 100);
    let data: any = {
      url: `admin/user/${id}`,
      method: "GET",
    };
    api.call(data, (res: any) => {
      processState("result", res.status);
      if (res.status === 200) {
        let result = res.data;
        setResultList(result);
        setMultiValueA({
          firstName: result.firstName || "",
          lastName: result.lastName || "",
          email: result.email || "",
          title: result.title || "",
          mobile: result.mobile || "",
          password: "",
          role: result.role ? getRole(result.role) : null,
          verified: result.verified,
          active: result.active == true ? false : true,
        });
        setMultiValueB({
          firstName: result.firstName || "",
          lastName: result.lastName || "",
          email: result.email || "",
          title: result.title || "",
          mobile: result.mobile || "",
          password: "",
          role: result.role ? getRole(result.role) : null,
          verified: result.verified,
          active: result.active == true ? false : true,
        });
      }
    });
  };

  const update = () => {
    let data = common.formPayload(fieldsB, ["mobile"]);
    data.role = fieldsB.role.value;
    data.mobile = fieldsB.mobile
      ? fieldsB.mobile.toString().replace(/[\(\)\-\s]/g, "")
      : "";
    data.active = fieldsB.active == true ? false : true;
    data.verified = fieldsB.verified || false;
    if (!data.password) {
      delete data.password;
    }

    let param = {
      url: `admin/user/${id}`,
      method: "PUT",
      body: data,
    };

    api.call(param, (res: any) => {
      if (res.status === 200) {
        common.notify("S", "User updated successfully");
        navigate(`/user/form?id=${id}&mode=view`);
        processState("result", 100);
        getData();
      } else {
        common.notify("E", res.message);
      }
      setSubmit("");
    });
  };

  const add = () => {
    let data = common.formPayload(fieldsA, ["mobile"]);
    data.role = fieldsA.role.value;
    data.mobile = fieldsA.mobile ? fieldsA.mobile.replace(/[\(\)\-\s]/g, "") : "";
    data.active = fieldsA.active == true ? false : true;
    data.verified = fieldsA.verified || false;
    if (!data.password) {
      delete data.password;
    }

    let param = {
      url: `admin/user`,
      method: "POST",
      body: data,
    };

    api.call(param, (res: any) => {
      if (res.status === 200) {
        common.notify("S", "User added successfully");
        navigate(`/user/list`);
      } else if (res.status === 500) {
        common.notify("E", "Email address already exist");
      } else {
        common.notify("E", res.message);
      }
      setSubmit("");
    });
  };

  const deleteUser = () => {
    let data: any = {
      url: `admin/user/${confirm.id}`,
      method: "DELETE",
    };

    api.call(data, (res: any) => {
      if (res.status === 200) {
        setConfirm({ id: null, type: "" });
        common.notify("S", "User deleted successfully");
        navigate(`/user/list`);
      } else {
        common.notify("E", res.error);
      }
    });
  };

  // events
  const onSubmit = (e: any) => {
    e.preventDefault();
    if (id) {
      const isValid = handleSubmitB();
      if (
        isValid &&
        ((fieldsB.password && fieldsB.password.length > 7) ||
          fieldsB?.password?.length === 0)
      ) {
        setSubmit("submit");
        update();
      } else if (fieldsB.password && fieldsB.password.length < 8) {
        common.notify("E", "Password Length Should Be Eight");
      }
    } else {
      const isValid = handleSubmitA();
      if (isValid) {
        setSubmit("submit");
        add();
      }
    }
  };

  const deleteCallBack = (isDelete: boolean) => {
    if (isDelete) {
      deleteUser();
    } else {
      setConfirm({ id: null, type: "" });
    }
  };

  // callback
  const onAgCallBack = (status: any) => {
    getData();
  };

  // support
  const getRole = (value: any) => {
    let filtered = config.options.roles.filter((e: any) => e.value === value);
    if (filtered.length > 0) {
      return filtered[0];
    } else return null;
  };
  const processState = (label: any, value: any) => {
    setProcess((prev: any) => ({
      ...prev,
      [label]: value,
    }));
  };

  return (
    <Fragment>
      <PageBreadcrumb
        breadCrumbItems={[
          { label: "Users", path: "/user/list", active: false },
          { label: "User Form", path: "/user/form", active: true },
        ]}
        title={"User Form"}
      />
      {process.result != 200 ? (
        <AgStatus
          process={process.result}
          message={{
            100: "Loading... Please wait!",
            404: "Something went wrong! Please Refresh Again",
          }}
          button={{ 400: "Refresh", 404: "Refresh" }}
          size="l"
          onAgCallBack={onAgCallBack}
        />
      ) : (
        <Fragment>
          {mode != "view" ? (
            <Fragment>
              {" "}
              <Row>
                <Col xl={12} lg={12}>
                  <Card>
                    <Card.Body className="py-1 px-1 border-bottom border-light">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="float-start">
                          <OverlayTrigger
                            placement="bottom"
                            overlay={<Tooltip id="">Back</Tooltip>}
                          >
                            <Link
                              to="/user/list"
                              className="text-reset fs-20 p-1 d-inline-block"
                            >
                              <i
                                className="ri-arrow-left-s-line pe-2"
                                data-bs-container="#tooltips-container"
                                data-bs-toggle="tooltip"
                              />
                            </Link>
                          </OverlayTrigger>
                          <label className="form-check-label fs-16">
                            {id ? "Edit" : "Add"} User
                          </label>
                        </div>
                        <div id="tooltips-container d-flex align-items-center">
                          <div className="d-flex flex-wrap gap-2">
                            <Button
                              variant="secondary"
                              onClick={() => {
                                navigate("/user/list");
                              }}
                              disabled={submit === "submit"}
                            >
                              <i className=" ri-arrow-go-back-line me-1" />
                              Cancel
                            </Button>
                            <Button
                              variant="primary"
                              onClick={onSubmit}
                              disabled={submit === "submit"}
                            >
                              {submit === "submit" ? (
                                <>
                                  <Spinner
                                    className="spinner-border-sm me-1"
                                    color="white"
                                  />
                                  Processing...
                                </>
                              ) : (
                                <>
                                  <i className="ri-save-line" /> Save
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Card>
                    <Card.Body>
                      {!id ? (
                        <Form>
                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>First Name</Form.Label>
                                <Form.Control
                                  type="text"
                                  id="singleLine"
                                  maxLength={100}
                                  data-toggle="maxlength"
                                  placeholder="Enter first name"
                                  name="firstName"
                                  value={fieldsA.firstName}
                                  onChange={handleChangeA}
                                  isInvalid={errorsA.firstName ? true : false}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errorsA.firstName}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Last Name</Form.Label>
                                <Form.Control
                                  type="text"
                                  name="lastName"
                                  id="singleLine"
                                  maxLength={100}
                                  data-toggle="maxlength"
                                  placeholder="Enter last name"
                                  value={fieldsA.lastName}
                                  onChange={handleChangeA}
                                  isInvalid={errorsA.lastName ? true : false}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errorsA.lastName}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                            {!id && (
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Email</Form.Label>
                                  <Form.Control
                                    type="email"
                                    name="email"
                                    id="singleLine"
                                    placeholder="Enter Email"
                                    value={fieldsA.email}
                                    onChange={handleChangeA}
                                    autoComplete="new-email"
                                    isInvalid={errorsA.email ? true : false}
                                  />
                                  <Form.Control.Feedback type="invalid">
                                    {errorsA.email}
                                  </Form.Control.Feedback>
                                </Form.Group>
                              </Col>
                            )}
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Mobile</Form.Label>
                                <MaskedInput
                                  mask={[
                                    "(",
                                    /[1-9]/,
                                    /\d/,
                                    /\d/,
                                    ")",
                                    " ",
                                    /\d/,
                                    /\d/,
                                    /\d/,
                                    "-",
                                    /\d/,
                                    /\d/,
                                    /\d/,
                                    /\d/,
                                  ]}
                                  placeholder="Enter Mobile"
                                  className="form-control"
                                  value={fieldsA.mobile}
                                  onChange={(e) =>
                                    setValueA("mobile", e.target.value)
                                  }
                                />
                                {errorsA.mobile && (
                                  <span className="text-danger fs-6">
                                    {errorsA.mobile}
                                  </span>
                                )}
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Role</Form.Label>
                                <Select
                                  options={config.options.roles}
                                  isOptionDisabled={(option) => option.disabled}
                                  value={fieldsA.role}
                                  onChange={(e) => setValueA("role", e)}
                                  classNamePrefix="react-select"
                                  className={
                                    errorsA.role
                                      ? "select2 z-5 required-select-input"
                                      : "select2 z-5"
                                  }
                                />
                                {errorsA.role && (
                                  <span className="text-danger fs-6">
                                    {errorsA.role}
                                  </span>
                                )}
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Password</Form.Label>
                                <Form.Control
                                  type="password"
                                  name="password"
                                  placeholder="Enter Password"
                                  value={fieldsA.password}
                                  onChange={handleChangeA}
                                  isInvalid={errorsA.password ? true : false}
                                  maxLength={8}
                                  data-toggle="maxlength"
                                  autoComplete="new-password"
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errorsA.password}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                          </Row>
                        </Form>
                      ) : (
                        // Update Form
                        <Form>
                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>First Name</Form.Label>
                                <Form.Control
                                  type="text"
                                  id="singleLine"
                                  maxLength={100}
                                  data-toggle="maxlength"
                                  placeholder="Enter first name"
                                  name="firstName"
                                  value={fieldsB.firstName}
                                  onChange={handleChangeB}
                                  isInvalid={errorsB.firstName ? true : false}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errorsB.firstName}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Last Name</Form.Label>
                                <Form.Control
                                  type="text"
                                  name="lastName"
                                  id="singleLine"
                                  maxLength={100}
                                  data-toggle="maxlength"
                                  placeholder="Enter last name"
                                  value={fieldsB.lastName}
                                  onChange={handleChangeB}
                                  isInvalid={errorsB.lastName ? true : false}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errorsB.lastName}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                            {!id && (
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Email</Form.Label>
                                  <Form.Control
                                    type="email"
                                    name="email"
                                    id="singleLine"
                                    placeholder="Enter Email"
                                    value={fieldsB.email}
                                    onChange={handleChangeB}
                                    autoComplete="new-email"
                                    isInvalid={errorsB.email ? true : false}
                                  />
                                  <Form.Control.Feedback type="invalid">
                                    {errorsB.email}
                                  </Form.Control.Feedback>
                                </Form.Group>
                              </Col>
                            )}
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Mobile</Form.Label>
                                <MaskedInput
                                  mask={[
                                    "(",
                                    /[1-9]/,
                                    /\d/,
                                    /\d/,
                                    ")",
                                    " ",
                                    /\d/,
                                    /\d/,
                                    /\d/,
                                    "-",
                                    /\d/,
                                    /\d/,
                                    /\d/,
                                    /\d/,
                                  ]}
                                  placeholder="Enter Mobile"
                                  className="form-control"
                                  value={fieldsB.mobile}
                                  onChange={(e) =>
                                    setValueB("mobile", e.target.value)
                                  }
                                />
                                {errorsB.mobile && (
                                  <span className="text-danger fs-6">
                                    {errorsB.mobile}
                                  </span>
                                )}
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Role</Form.Label>
                                <Select
                                  options={config.options.roles}
                                  isOptionDisabled={(option) => option.disabled}
                                  value={fieldsB.role}
                                  onChange={(e) => setValueB("role", e)}
                                  classNamePrefix="react-select"
                                  className={
                                    errorsB.role
                                      ? "select2 z-5 required-select-input"
                                      : "select2 z-5"
                                  }
                                />
                                {errorsB.role && (
                                  <span className="text-danger fs-6">
                                    {errorsB.role}
                                  </span>
                                )}
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Change Password</Form.Label>
                                <Form.Control
                                  type="password"
                                  name="password"
                                  placeholder="Enter Password"
                                  value={fieldsB.password}
                                  onChange={handleChangeB}
                                  isInvalid={errorsB.password ? true : false}
                                  maxLength={8}
                                  autoComplete="new-password"
                                  data-toggle="maxlength"
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errorsB.password}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Title</Form.Label>
                                <Form.Control
                                  type="text"
                                  id="singleLine"
                                  maxLength={100}
                                  data-toggle="maxlength"
                                  placeholder="Enter title"
                                  name="title"
                                  value={fieldsB.title}
                                  onChange={handleChangeB}
                                  isInvalid={errorsB.title ? true : false}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errorsB.title}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label htmlFor="suspended">
                                  Suspended
                                </Form.Label>
                                <Form.Check
                                  label="Suspended"
                                  checked={fieldsB.active}
                                  onChange={(e) => {
                                    setValueB("active", e.target.checked);
                                  }}
                                  id="suspended"
                                />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label htmlFor="verified">
                                  Verified
                                </Form.Label>
                                <Form.Check
                                  label="Verified"
                                  checked={fieldsB.verified}
                                  onChange={(e) =>
                                    setValueB("verified", e.target.checked)
                                  }
                                  id="verified"
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                        </Form>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Fragment>
          ) : (
            <Fragment>
              <Row>
                <Col xl={12} lg={12}>
                  <Card>
                    <Card.Body className="py-1 px-1 border-bottom border-light">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="float-start">
                          <OverlayTrigger
                            placement="bottom"
                            overlay={<Tooltip id="">Back</Tooltip>}
                          >
                            <Link
                              to="/user/list"
                              className="text-reset fs-20 p-1 d-inline-block"
                            >
                              <i
                                className="ri-arrow-left-s-line pe-2"
                                data-bs-container="#tooltips-container"
                                data-bs-toggle="tooltip"
                              />
                            </Link>
                          </OverlayTrigger>
                          <label className="form-check-label fs-16">
                            User Detail
                          </label>
                        </div>
                        {permission.includes("usu") && (
                          <div id="tooltips-container d-flex align-items-center">
                            <div className="d-flex flex-wrap gap-2">
                              <OverlayTrigger
                                placement="bottom"
                                overlay={<Tooltip id="">Edit</Tooltip>}
                              >
                                <Button
                                  className="btn btn-light"
                                  onClick={() => {
                                    navigate(`/user/form?id=${id}&mode=edit`);
                                  }}
                                >
                                  <i
                                    className="ri-edit-2-line"
                                    data-bs-container="#tooltips-container"
                                    data-bs-toggle="tooltip"
                                  />
                                </Button>
                              </OverlayTrigger>
                            </div>
                          </div>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              <Card>
                <Card.Body>
                  <h5 className="mb-3 text-uppercase bg-light p-2">
                    <i className="ri-contacts-book-2-line me-1" /> Personal
                    Information
                  </h5>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>First Name</Form.Label>
                        <p className="text-muted fs-14">
                          {resultList.firstName || "-"}
                        </p>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Last Name</Form.Label>
                        <p className="text-muted fs-14">
                          {resultList.lastName || "-"}
                        </p>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Email</Form.Label>
                        <p className="text-muted fs-14">
                          {resultList.email || "-"}
                        </p>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Mobile Number</Form.Label>
                        <p className="text-muted fs-14">
                          {resultList.mobile || "-"}
                        </p>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Status</Form.Label>
                        <p className="text-muted fs-14">
                          {Boolean(resultList.verified) == true
                            ? "Verified"
                            : "Not Verified"}
                        </p>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Last Login</Form.Label>
                        <p className="text-muted fs-14">
                          {resultList.lastLogin
                            ? moment(resultList.lastLogin).format(
                              "DD MMM YYYY hh:mm A"
                            )
                            : "-"}
                        </p>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Title</Form.Label>
                        <p className="text-muted fs-14">
                          {resultList.title || "-"}
                        </p>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Suspended</Form.Label>
                        <p className="text-muted fs-14">
                          {Boolean(resultList.active) == true ? "No" : "Yes"}
                        </p>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Role</Form.Label>
                        <p className="text-muted fs-14">
                          {resultList.role || "-"}
                        </p>
                      </Form.Group>
                    </Col>
                    {permission.includes("usd") && (
                      <Col md={4}>
                        <Button
                          variant="danger"
                          size="sm"
                          className="me-1 d-felx align-items-center"
                          onClick={() => setConfirm({ id: id, type: "user" })}
                        >
                          <i className="ri-delete-bin-line fs-16" />
                          <span className="ms-1">Permanently Delete User</span>
                        </Button>
                      </Col>
                    )}
                  </Row>
                </Card.Body>
              </Card>
              <NotificationPreference userId={id} />
            </Fragment>
          )}
        </Fragment>
      )}
      {/* Delete confirmation */}
      {confirm.id && (
        <AgDelete type="delete" onAgDeleteCallBack={deleteCallBack} />
      )}
    </Fragment>
  );
}

export const localities = [
  { value: "Brroklyn: New York City", label: "Brroklyn: New York City" },
  { value: "Manhattan: New York City", label: "Manhattan: New York City" },
];
